import React from "react";
import { createPortal } from "react-dom";
import { IModalProps } from "../interfaces/others/IModal";

const ModalContainer: React.FC<IModalProps> = ({
  open,
  title,
  closeFn,
  size,
  children,
  ...props
}) => {
  const getMaxWidth = () => {
    switch (size) {
      case "sm":
        return "400px";
      case "lg":
        return "900px";
      case "xl":
        return "1200px";
      case "xxl":
        return "min(96vw, 1480px)";
      default:
        return "600px";
    }
  };

  if (!open) return null;

  const handleCloseOnOutsideClick = (event: React.MouseEvent) => {
    if (!closeFn) return;

    const target = event.target as Element;

    if (target.classList.contains("modal-overlay")) {
      closeFn(event);
    }
  };

  const backdropStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 1040,
    animation: "fadeIn 0.2s ease-in-out",
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    overflowY: "auto",
  };

  const modalDialogStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: getMaxWidth(),
    margin: "auto",
    animation: "slideUp 0.3s ease-out",
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
    position: "relative",
  };

  const modalHeaderStyle: React.CSSProperties = {
    padding: "1.25rem 1.5rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
  };

  const modalTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#1f2937",
  };

  const closeButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#6b7280",
    padding: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    width: "32px",
    height: "32px",
    lineHeight: 1,
  };

  const modalBodyStyle: React.CSSProperties = {
    padding: "1.5rem",
    maxHeight: "calc(100vh - 200px)",
    minHeight:
      size === "lg" || size === "xxl" ? "min(70vh, 560px)" : "auto",
    overflowY: "auto",
  };

  const modalTree = (
    <React.Fragment>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .modal-close-btn:hover {
            background-color: #f3f4f6 !important;
            color: #1f2937 !important;
          }

          .modal-close-btn:active {
            background-color: #e5e7eb !important;
          }
        `}
      </style>

      {/* Backdrop */}
      <div style={backdropStyle}></div>

      {/* Modal */}
      <div
        className="modal-overlay"
        style={modalOverlayStyle}
        onClick={handleCloseOnOutsideClick}
        ref={props?.refModal}
      >
        <div style={modalDialogStyle} onClick={(e) => e.stopPropagation()}>
          <div className={props.classModal} style={modalContentStyle}>
            {/* Header */}
            <div style={modalHeaderStyle}>
              <h5 style={modalTitleStyle}>{title}</h5>
              {closeFn && (
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={closeFn}
                  style={closeButtonStyle}
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Body */}
            <div style={modalBodyStyle}>{children}</div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  return createPortal(modalTree, document.body);
};

export default ModalContainer;
